import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  IoClose,
  IoPersonAddOutline,
  IoTrashOutline,
  IoBusinessOutline,
} from 'react-icons/io5';
import { IoPeopleOutline } from 'react-icons/io5';

import {
  fetchNewUserAccessList,
  addNewUserAccess,
  deleteNewUserAccess,
} from '../../services/api';
import Pagination from './Pagination';
import { confirmDialog } from '../../components/ConfirmDialog';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(dateString));
};

const ACCESS_LEVEL_COLORS = {
  basic: 'bg-gray-500/10 text-gray-400',
  free: 'bg-green-500/10 text-green-400',
  paid: 'bg-[#5652CF]/10 text-blue-400',
  premium: 'bg-purple-500/10 text-purple-400',
  default: 'bg-gray-500/10 text-gray-400',
};

// +++ NEW: MODAL COMPONENT FOR PRE-APPROVED USERS +++
const PreApprovedUserModal = ({
  isOpen,
  onClose,
  userIdToken,
  accessLevelsConfig,
}) => {
  const [data, setData] = useState({
    entries: [],
    total_pages: 1,
    current_page: 1,
  });
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Form state
  const [newEmail, setNewEmail] = useState('');
  const [newAccessLevel, setNewAccessLevel] = useState('basic');

  // NEW: State for the enterprise access toggle
  const [newEnterpriseAccess, setNewEnterpriseAccess] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const fetchList = async (page = 1) => {
        setLoading(true);
        try {
          const result = await fetchNewUserAccessList(userIdToken, { page });
          setData(result);
        } catch (error) {
          toast.error(`Failed to load list: ${error.toString()}`);
        } finally {
          setLoading(false);
        }
      };
      fetchList(data.current_page);
    }
  }, [isOpen, userIdToken, refreshKey, data.current_page]);

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= data.total_pages) {
      setData((prev) => ({ ...prev, current_page: newPage }));
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!newEmail) {
      toast.error('Email is required.');
      return;
    }
    setIsSubmitting(true);
    const toastId = toast.loading('Adding user...');
    try {
      // NEW: Include enterprise_access in the payload
      const payload = {
        email: newEmail,
        access_level: newAccessLevel,
        enterprise_access: newEnterpriseAccess,
      };
      await addNewUserAccess(userIdToken, payload);
      toast.success('User added to pre-approved list!', { id: toastId });
      setNewEmail(''); // Reset form
      setNewEnterpriseAccess(false); // Reset toggle
      setRefreshKey((prev) => prev + 1); // Refresh list
    } catch (error) {
      toast.error(`Error: ${error.toString()}`, { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (email) => {
    if (
      !(await confirmDialog({
        title: `Remove ${email} from the pre-approved list?`,
        confirmLabel: 'Remove',
        danger: true,
      }))
    )
      return;

    const toastId = toast.loading(`Deleting ${email}...`);
    try {
      await deleteNewUserAccess(userIdToken, email);
      toast.success('User removed successfully.', { id: toastId });
      setRefreshKey((prev) => prev + 1); // Refresh list
    } catch (error) {
      toast.error(`Failed to delete: ${error.toString()}`, { id: toastId });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
      <div
        className="animate-fade-in-up m-4 flex w-full max-w-2xl flex-col rounded-xl border border-zinc-800 bg-zinc-900 shadow-2xl"
        style={{ maxHeight: '90vh' }}
      >
        <div className="flex items-center justify-between border-b border-zinc-700/50 p-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-zinc-200">
            <IoPeopleOutline /> Pre-Approved User Access
          </h3>
          <button
            onClick={onClose}
            className="rounded-full p-1 transition-colors hover:bg-zinc-800"
          >
            <IoClose className="h-5 w-5 text-zinc-400" />
          </button>
        </div>

        {/* Add User Form */}
        {/* MODIFIED: Add User Form */}
        <form
          onSubmit={handleAddUser}
          className="flex flex-row flex-col items-end gap-3 border-b border-zinc-700/50 p-4"
        >
          <div className="w-full flex-grow">
            <label
              htmlFor="email-address"
              className="mb-1 block text-xs font-medium text-zinc-400"
            >
              Email Address
            </label>
            <input
              type="email"
              placeholder="new.user@example.com"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              required
              className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            />
          </div>
          <div className="w-full">
            <label
              htmlFor="access-level"
              className="mb-1 block text-xs font-medium text-zinc-400"
            >
              Access Level
            </label>
            <select
              value={newAccessLevel}
              onChange={(e) => setNewAccessLevel(e.target.value)}
              className="w-full rounded-lg border border-zinc-700 bg-zinc-800 px-3 py-2 text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            >
              {accessLevelsConfig &&
                Object.entries(accessLevelsConfig).map(([key, config]) => (
                  <option
                    className={`${ACCESS_LEVEL_COLORS[key]}`}
                    key={key}
                    value={key}
                  >
                    {config.label}
                  </option>
                ))}
            </select>
          </div>
          {/* NEW: Enterprise Access Toggle */}
          <div className="flex h-[42px] w-full items-center">
            <label className="flex cursor-pointer select-none items-center">
              <input
                type="checkbox"
                checked={newEnterpriseAccess}
                onChange={(e) => setNewEnterpriseAccess(e.target.checked)}
                className="sr-only"
              />
              <div
                className={`flex h-6 w-11 items-center rounded-full p-1 duration-300 ${newEnterpriseAccess ? 'bg-green-500' : 'bg-zinc-800'}`}
              >
                <div
                  className={`h-4 w-4 transform rounded-full bg-white shadow-md duration-300 ${newEnterpriseAccess ? 'translate-x-5' : ''}`}
                ></div>
              </div>
              <span className="ml-3 text-sm font-medium text-zinc-200">
                Enterprise
              </span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="flex w-full items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            <IoPersonAddOutline /> {isSubmitting ? 'Adding...' : 'Add User'}
          </button>
        </form>

        {/* User List */}
        {/* MODIFIED: User List to show enterprise status */}
        <div className="flex-grow overflow-y-auto p-4">
          {loading ? (
            <p className="text-center text-zinc-400">Loading list...</p>
          ) : data.entries.length === 0 ? (
            <p className="text-center text-zinc-400">
              No pre-approved users found.
            </p>
          ) : (
            <div className="space-y-2">
              {data.entries.map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-wrap items-start justify-between gap-4 rounded-lg bg-zinc-800 p-3 md:flex-nowrap md:items-center"
                >
                  <div>
                    <p className="font-semibold text-zinc-200">{entry.email}</p>
                    <p className="text-xs text-zinc-400">
                      Access:{' '}
                      <span
                        className={`font-medium capitalize ${ACCESS_LEVEL_COLORS[entry.access_level]}`}
                      >
                        {entry.access_level}
                      </span>{' '}
                      | Added: {formatDate(entry.created_at)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* NEW: Enterprise Status Badge */}
                    {entry.enterprise_access && (
                      <span
                        title="Enterprise Access Enabled"
                        className="flex items-center gap-1 rounded-full bg-[#5652CF]/10 px-2 py-1 text-xs font-medium text-blue-400"
                      >
                        <IoBusinessOutline /> Enterprise
                      </span>
                    )}
                    {entry.signed_up ? (
                      <span className="rounded-full bg-green-500/10 px-2.5 py-0.5 text-xs font-medium text-green-400">
                        Signed Up
                      </span>
                    ) : (
                      <span className="rounded-full bg-yellow-500/10 px-2.5 py-0.5 text-xs font-medium text-yellow-400">
                        Pending
                      </span>
                    )}
                    <button
                      onClick={() => handleDelete(entry.email)}
                      disabled={entry.signed_up}
                      className="rounded-full p-2 text-red-500 transition-colors hover:bg-red-500/10 disabled:cursor-not-allowed disabled:text-gray-500 disabled:hover:bg-transparent"
                    >
                      <IoTrashOutline className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Pagination */}
        {data.total_pages > 1 && (
          <div className="flex items-center justify-center border-t border-zinc-700/50 p-4">
            <Pagination
              currentPage={data.current_page}
              totalPages={data.total_pages}
              onPageChange={handlePageChange}
              totalUsers={data.total_entries}
              limit={10}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default PreApprovedUserModal;
